import Dispatcher from "../dispatcher";
Dispatcher.OVERLEAF_CHANGE = "Overleaf.Change";
Dispatcher.OVERLEAF_DECKS_UPDATED = "Overleaf.Decks.Loaded";
Dispatcher.OVERLEAF_DECK_UPDATED = "Overleaf.Deck.Loaded";
Dispatcher.OVERLEAF_CARD_RATED = "Overleaf.Card.Rated";
import deepmerge from "deepmerge";
import {
  convertContentUrlToFullUrl,
  convertFullUrlToContentUrl
} from "../utils/url-utils";

export const TYPE_IMAGE = "image";
export const TYPE_TEXT = "text";

const _defaultState = {
  decksLoaded: false,
  decksObj: {},
  decksArray: []
};

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
const freeze = obj => {
  const propNames = Object.getOwnPropertyNames(obj);
  propNames.forEach(function(name) {
    var prop = obj[name];

    if (typeof prop == "object" && prop !== null) freeze(prop);
  });
  return Object.freeze(obj);
};

const copyUnfrozen = obj => {
  let newObj;
  if (obj instanceof Array) {
    newObj = obj.slice();
  } else {
    newObj = Object.assign({}, obj);
  }
  const propNames = Object.getOwnPropertyNames(obj);
  propNames.forEach(function(name) {
    var prop = obj[name];

    if (typeof prop == "object" && prop !== null)
      newObj[name] = copyUnfrozen(prop);
  });
  return newObj;
};

let _state = freeze(_defaultState);

const OverleafStore = {
  CONFIDENCE: {
    CONFIDENT: 1,
    NEUTRAL: 0,
    NOT_CONFIDENT: -1
  },

  allDecksPromise: null,
  _state: {
    decksLoaded: false,
    decks: {}
  },
  loadingDecks: {},

  _updateState(state) {
    _state = Object.assign({}, _state, state);
    if (Fraction.debugger) {
      freeze(_state);
    }
    Dispatcher.emit(Dispatcher.OVERLEAF_CHANGE);
  },

  reset() {
    this._updateState(freeze(_defaultState));
  },

  get(id) {
    this._load();
    return _state.decksObj[id];
  },

  cleanCardToPostSide(side) {
    if (side.type === TYPE_IMAGE) {
      side.content = convertFullUrlToContentUrl(side.content);
    }
  },

  cleanCardToPost(card) {
    const newCard = deepmerge({}, card);
    this.cleanCardToPostSide(newCard.front);
    this.cleanCardToPostSide(newCard.back);
    return newCard;
  },

  augmentSide(side) {
    if (side.type === TYPE_IMAGE) {
      side.content = convertContentUrlToFullUrl(side.content);
    }
  },

  augmentCard(card, deck) {
    card.confidence = this.getConfidence(card);
    card.deckUUID = deck.uuid;
    this.augmentSide(card.front);
    this.augmentSide(card.back);
    return card;
  },

  augmentDeck(deck) {
    if (deck.cards) {
      deck.cards = deck.cards.map(card => this.augmentCard(card, deck));
    }
    return deck;
  },

  loadDecks(meta) {
    if (!Overleaf) {
      return new Promise(() => {
        // eslint-disable-next-line no-throw-literal
        throw "Overleaf down";
      });
    }
    return (this.loadingDecksPromise =
      this.loadingDecksPromise ||
      Overleaf.init().then(() =>
        Overleaf.Decks.all(meta)
          .then(response => {
            const decksArray = [];
            const decksObj = response.decks.reduce((acc, deck) => {
              const augmentedDeck = this.augmentDeck(deck);
              decksArray.push(augmentedDeck);
              acc[augmentedDeck.uuid] = augmentedDeck;
              return acc;
            }, {});

            this._updateState({ decksLoaded: true, decksObj, decksArray });
            this.loadingDecksPromise = null;
            return _state.decksArray;
          })
          .catch(response => {
            this.loadingDecksPromise = null;
            throw response;
          })
      ));
  },

  getDecks(meta) {
    if (_state.decksLoaded) {
      return Promise.resolve(_state.decksArray);
    }

    return this.loadDecks(meta);
  },

  loadDeck(deckId) {
    return (this.loadingDecks[deckId] =
      this.loadingDecks[deckId] ||
      Overleaf.init().then(() => {
        return Overleaf.Decks.one(deckId)
          .then(response => {
            let deck = this.augmentDeck(response.deck);
            this._updateState({
              decksObj: Object.assign({}, _state.decksObj, { [deckId]: deck })
            });
            delete this.loadingDecks[deckId];
            return copyUnfrozen(deck);
          })
          .catch(response => {
            delete this.loadingDecks[deckId];
            throw response;
          });
      }));
  },

  getDeck(deckId) {
    const deck = _state.decksObj[deckId];
    if (deck && deck.cards) {
      return Promise.resolve(copyUnfrozen(deck));
    }

    return this.loadDeck(deckId);
  },

  getCards(deckId) {
    const deck = _state.decksObj[deckId];
    if (deck && deck.cards) {
      return deck.cards.map(card => deepmerge({}, card));
    }
    return [];
  },

  createDeck(deck) {
    return (this.creatingDeckPromise =
      this.creatingDeckPromise ||
      Overleaf.init().then(() => {
        return Overleaf.Decks.create(deck)
          .then(response => {
            let deck = this.augmentDeck(response.deck);
            const decksArray = [deck].concat(_state.decksArray);
            const decksObj = Object.assign({}, _state.decksObj, {
              [deck.uuid]: deck
            });
            this._updateState({
              decksObj,
              decksArray
            });
            // not sure this is *really* necessary since it's a new deck (shrug)
            this.creatingDeckPromise = null;
            Dispatcher.emit(Dispatcher.OVERLEAF_DECK_UPDATED, deck.uuid);
            return deck;
          })
          .catch(response => {
            this.creatingDeckPromise = null;
            throw response;
          });
      }));
  },

  duplicateDeck(deckUUID, params) {
    return (this.duplicateDeckPromise =
      this.duplicateDeckPromise ||
      Overleaf.init().then(() => {
        return Overleaf.Decks.duplicate(deckUUID, params)
          .then(response => {
            let deck = this.augmentDeck(response.deck);
            const decksArray = [deck].concat(_state.decksArray);
            this._updateState({
              decksObj: Object.assign({}, _state.decksObj, {
                [deck.uuid]: deck
              }),
              decksArray
            });
            this.duplicateDeckPromise = null;
            Dispatcher.emit(Dispatcher.OVERLEAF_DECKS_UPDATED);
          })
          .catch(response => {
            this.duplicateDeckPromise = null;
            throw response;
          });
      }));
  },

  addCard(deckId, card) {
    return (this.createDeckPromise =
      this.createDeckPromise ||
      Overleaf.init().then(() => {
        return Overleaf.Cards.create(deckId, this.cleanCardToPost(card))
          .then(response => {
            // reload the deck (which will also update the state)

            const decksObj = Object.assign({}, _state.decksObj);
            const deck = Object.assign({}, decksObj[deckId]);
            const decksArray = _state.decksArray.slice();

            if (deck.cards) {
              deck.cards = [this.augmentCard(response.card, deck)].concat(
                deck.cards
              );
              deck.card_count = deck.card_count + 1;
              decksArray[decksArray.findIndex(d => d.uuid === deckId)] = deck;
              decksObj[deck.uuid] = deck;
              this._updateState({ decksObj, decksArray });
              this.createDeckPromise = null;
              return response.card;
            } else {
              this.createDeckPromise = null;
              return this.loadDeck(deckId).then(deck => {
                return response.card;
              });
            }
          })
          .catch(response => {
            this.createDeckPromise = null;
            throw response;
          });
      }));
  },

  addProficiency(deckId, cardId, prof) {
    return Overleaf.init().then(() => {
      return Overleaf.Proficiencies.create(deckId, cardId, prof)
        .then(response => {
          Dispatcher.emit(Dispatcher.OVERLEAF_CARD_UPDATED, {
            deckUUID: deckId,
            uuid: cardId
          });
          // do we also need to emit a OVERLEAF_DECK_UPDATED event?
          return response.proficiency;
        })
        .catch(response => {
          throw response;
        });
    });
  },

  updateDeck(deckId, deck) {
    return (this.loadingDecks[deckId] =
      this.loadingDecks[deckId] ||
      Overleaf.init().then(() => {
        return Overleaf.Decks.update(deckId, deck)
          .then(response => {
            const deck = this.augmentDeck(response.deck);
            const decksArray = _state.decksArray.slice();
            decksArray[decksArray.findIndex(d => d.uuid === deck.uuid)] = deck;
            const decksObj = Object.assign({}, _state.decksObj, {
              [deck.uuid]: deck
            });
            this._updateState({ decksObj, decksArray });
            delete this.loadingDecks[deckId];
            // emit *after* we've updated the state
            Dispatcher.emit(Dispatcher.OVERLEAF_DECK_UPDATED, deckId);
            return deck;
          })
          .catch(response => {
            delete this.loadingDecks[deckId];
            throw response;
          });
      }));
  },

  updateCard(deckId, cardId, card) {
    return Overleaf.init().then(() => {
      return Overleaf.Cards.update(deckId, cardId, this.cleanCardToPost(card))
        .then(response => {
          const decksObj = Object.assign({}, _state.decksObj);
          const deck = Object.assign({}, decksObj[deckId]);
          const decksArray = _state.decksArray.slice();
          const deckCards = deck.cards.slice();

          deckCards[
            deckCards.findIndex(c => c.uuid === response.card.uuid)
          ] = this.augmentCard(response.card, deck);

          deck.cards = deckCards;
          decksArray[decksArray.findIndex(d => d.uuid === deckId)] = deck;
          decksObj[deck.uuid] = deck;
          this._updateState({ decksObj, decksArray });
          return response.card;
        })
        .catch(response => {
          throw response;
        });
    });
  },

  deleteDeck(deckId) {
    return (this.loadingDecks[deckId] =
      this.loadingDecks[deckId] ||
      Overleaf.init().then(() => {
        return Overleaf.Decks.delete(deckId)
          .then(response => {
            // remove it from the order list and the state
            const decksArray = _state.decksArray.slice();
            decksArray.splice(
              decksArray.findIndex(deck => deck.uuid === deckId),
              1
            );
            const decksObj = Object.assign({}, _state.decksObj);
            delete decksObj[deckId];
            this._updateState({
              decksArray,
              decksObj
            });
            Dispatcher.emit(Dispatcher.OVERLEAF_DECKS_UPDATED);
          })
          .catch(response => {
            throw response;
          });
      }));
  },

  deleteCard(deckId, cardId) {
    return Overleaf.init().then(() => {
      return Overleaf.Cards.delete(deckId, cardId)
        .then(response => {
          const decksObj = Object.assign({}, _state.decksObj);
          const deck = Object.assign({}, decksObj[deckId]);
          const decksArray = _state.decksArray.slice();
          const deckCards = deck.cards.slice();

          deckCards.splice(deckCards.findIndex(c => c.uuid === cardId), 1);
          deck.card_count = deck.card_count > 0 ? deck.card_count - 1 : 0;
          deck.cards = deckCards;
          decksArray[decksArray.findIndex(d => d.uuid === deckId)] = deck;
          decksObj[deck.uuid] = deck;
          this._updateState({ decksObj, decksArray });
          return response.card;
        })
        .catch(response => {
          throw response;
        });
    });
  },

  getConfidence(card) {
    if (card.proficiencies.length === 0) {
      return undefined;
    }
    if (card.proficiencies[0].level > 75) {
      return 1;
    } else if (card.proficiencies[0].level > 25) {
      return 0;
    } else {
      return -1;
    }
  },

  _load(update) {},

  init() {
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() + 1);
    Dispatcher.bind(
      Dispatcher.OVERLEAF_SET_PROFICIENCY,
      (card, proficiency) => {
        const cardObj = card;
        Overleaf.Proficiencies.create(card.deckUUID, card.uuid, {
          level: proficiency
        }).then(response => {
          const deck = Object.assign({}, _state.decksObj[cardObj.deckUUID]);
          deck.cards = deck.cards.slice();

          const card = deepmerge(
            {},
            deck.cards.find(card => card.uuid === cardObj.uuid)
          );

          card.proficiencies = [response.proficiency].concat(
            card.proficiencies
          );
          deck.cards[deck.cards.findIndex(c => c.uuid === card.uuid)] = card;

          this.augmentCard(card, deck);
          const decksObj = Object.assign({}, _state, { [deck.uuid]: deck });
          const decksArray = _state.decksArray.slice();
          decksArray[decksArray.findIndex(d => d.uuid === deck.uuid)] = deck;
          this._updateState({
            decksObj,
            decksArray
          });
          Dispatcher.emit(Dispatcher.OVERLEAF_CARD_RATED, card.uuid);
          Dispatcher.emit(Dispatcher.OVERLEAF_DECK_UPDATED, deck.uuid);
        });
      }
    );
  }
};

OverleafStore.init();
window.OverleafStore = OverleafStore;
export default OverleafStore;
